import { z } from "zod";

/** The eras a world on Earth can be tagged with, latest first. */
const ERAS = [
  "Modern Day",
  "Cold War",
  "WW2",
  "Interwar",
  "WW1",
  "Age of Empires",
  "Age of Revolution",
  "Age of Absolutism",
  "Age of Enlightenment",
  "Late Middle Ages",
  "High Middle Ages",
  "Dark Ages",
  "Antiquity",
  "Bronze Age",
  "Stone Age",
  "Prehistoric",
] as const;

/**
 * Every category a preset is listed under, with the tags each allows. A preset has one of each.
 * Historical is our history as it was, and its tag is the era of its start date. Alt-Historical
 * changes the history and Historical Fiction adds to it, and both are tagged with the era the world
 * is in, whatever the year says.
 */
export const PRESET_CATEGORIES = {
  Historical: ERAS,
  "Alt-Historical": ERAS,
  "Historical Fiction": ERAS,
  "Science Fiction": ["Apocalypse", "Extraterrestrial", "Time Travel", "Future", "Abstract"],
  Fantasy: ["High Fantasy", "Low Fantasy"],
  "Creator Templates": ["Regions", "Prompts"],
} as const;

type CategoryName = keyof typeof PRESET_CATEGORIES;
type TagName = (typeof PRESET_CATEGORIES)[CategoryName][number];

const CATEGORY_NAMES = Object.keys(PRESET_CATEGORIES) as [CategoryName, ...CategoryName[]];
const TAG_NAMES = [...new Set(Object.values(PRESET_CATEGORIES).flat())] as [TagName, ...TagName[]];

export const PresetCategory = z
  .enum(CATEGORY_NAMES)
  .meta({ id: "PresetCategory", description: "What kind of world a preset is." });
export type PresetCategory = z.infer<typeof PresetCategory>;

export const PresetTag = z
  .enum(TAG_NAMES)
  .meta({ id: "PresetTag", description: "Its era or genre, one of its category's tags." });
export type PresetTag = z.infer<typeof PresetTag>;

/** Whether a tag is one of its category's. */
export function isTagOf(category: PresetCategory, tag: PresetTag): boolean {
  return (PRESET_CATEGORIES[category] as readonly string[]).includes(tag);
}
